import { Component, EventEmitter, Output, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { API_BASE_URL } from '../core/api-base-url.token';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EstablishmentsService, NearbyEstablishmentDto, PATIENT_LANGUAGES } from '../core/services/establishments.service';
import { OrganisationsService } from '../core/services/organisations.service';
import { ToastService } from '../core/services/toast.service';
import { flagIcon } from '../core/utils/flag-icon.util';
import * as QRCode from 'qrcode';

@Component({
  selector: 'app-qr-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './qr-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./qr-modal.component.scss']
})
export class QrModalComponent implements OnInit, OnChanges, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Input() establishmentId: string | null = null;
  @Input() inline = false;

  private http = inject(HttpClient);
  private base = inject(API_BASE_URL);
  private sanitizer = inject(DomSanitizer);
  private estabService = inject(EstablishmentsService);
  private organisationsService = inject(OrganisationsService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);

  establishmentName = '';
  queues: any[] = [];
  selectedQueueId = '';
  linksPublic = '#';
  linksWidget = '';
  expandedSection: string | null = 'etablissement';
  showCopyToast = false;
  establishmentLangues: string[] = ['fr'];
  widgetEnabled = false;
  togglingWidget = false;
  orgSlug: string | null = null;
  private orgId: string | null = null;
  searchEnabled = true;
  togglingSearchEnabled = false;
  prescripteurVisible = true;
  togglingPrescripteurVisible = false;

  nearbySelected: NearbyEstablishmentDto[] = [];
  nearbyCandidates: NearbyEstablishmentDto[] = [];
  nearbyPickerId = '';
  nearbyLoading = false;
  nearbyMutating = false;

  toggleSection(section: string) {
    this.expandedSection = this.expandedSection === section ? null : section;
  }

  ngOnInit() {
    this.loadEstablishment();
    this.loadNearby();
    if (!this.inline) {
      document.body.style.overflow = 'hidden';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // La modale peut rester montée (onglet "Kit de communication" inline) pendant
    // qu'on change d'établissement via le sélecteur d'en-tête : ngOnInit ne se
    // redéclenche pas dans ce cas, donc on recharge ici quand l'input change.
    if (changes['establishmentId'] && !changes['establishmentId'].firstChange) {
      this.loadEstablishment();
      this.loadNearby();
    }
  }

  ngOnDestroy() {
    if (!this.inline) {
      document.body.style.overflow = '';
    }
  }

  private loadEstablishment() {
    if (!this.establishmentId) {
      console.warn('Aucun ID d\'établissement fourni');
      return;
    }

    this.http.get(`${this.base}/api/v1/establishments/${this.establishmentId}`).subscribe({
      next: (data: any) => {
        this.establishmentName = data.name || '';
        this.queues = data.queues || [];
        this.selectedQueueId = this.queues[0]?.id || '';
        this.linksPublic = data.links?._public || '#';
        this.linksWidget = data.links?.widget || '';
        this.widgetEnabled = data.widget_enabled || false;
        this.establishmentLangues = data.langues?.length ? data.langues : ['fr'];
        this.prescripteurVisible = data.prescripteur_visible !== false;
        if (data.org_id) {
          this.orgId = data.org_id;
          this.organisationsService.getDetails(data.org_id).subscribe({
            next: (org) => {
              this.orgSlug = org.slug;
              this.searchEnabled = org.prescripteur_search_enabled !== false;
              this.generatePrescripteurPreview();
            },
            error: () => {},
          });
        }
      },
      error: (err) => {
        console.error('Erreur chargement établissement:', err);
        alert(this.translate.instant('componentsWizard.qrModal.alerts.cannotLoadEstablishment'));
      }
    });
  }

  get qrUrl(): string {
    const base = 'https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=';
    const fullUrl = this.linksPublic.startsWith('http') ? this.linksPublic + '/mobile' : this.base + this.linksPublic + '/mobile';
    return base + encodeURIComponent(fullUrl);
  }

  queueQrUrl(): string {
    const base = 'https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=';
    return base + encodeURIComponent(this.computeQueuePublicUrl());
  }

  private computeQueuePublicUrl(): string {
    const selectedQueue = this.queues.find(q => q.id === this.selectedQueueId);
    if (!selectedQueue) return this.linksPublic;
    if (selectedQueue.public_slug) return `${this.base}/public/queue/${selectedQueue.public_slug}`;
    return this.linksPublic;
  }

  copyEstablishmentUrl(): void {
    const url = this.linksPublic.startsWith('http') ? this.linksPublic + '/mobile' : this.base + this.linksPublic + '/mobile';
    navigator.clipboard?.writeText(url);
  }

  private computeMobileV2BaseUrl(): string {
    return this.linksPublic.startsWith('http') ? this.linksPublic + '/mobile-v2' : this.base + this.linksPublic + '/mobile-v2';
  }

  private computeTvBaseUrl(): string {
    return this.linksPublic.startsWith('http') ? this.linksPublic + '/tv' : this.base + this.linksPublic + '/tv';
  }

  getPatientPageUrl(): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.computeMobileV2BaseUrl());
  }

  /** Une ligne par langue active du site, réutilisée par l'aperçu mobile ET l'écran TV (2026-09-02)
   *  -- simple liste de langues actives, pas propre à un écran en particulier malgré son nom. */
  get patientScreenLanguages(): { code: string; label: string }[] {
    return this.establishmentLangues.map(code => ({
      code,
      label: PATIENT_LANGUAGES.find(l => l.code === code)?.label ?? code,
    }));
  }

  /** Section "Écran TV" dédiée (2026-09-02), distincte de l'aperçu mobile ci-dessus -- qui n'a plus
   *  de bouton "ouvrir" propre (le mockup embarqué suffit), voir {@link getPatientPageUrl}. */
  openTvScreenFor(lang: string): void {
    window.open(this.computeTvBaseUrl() + '?lang=' + lang, '_blank');
  }

  prescripteurQrDataUrl: string | null = null;

  get prescripteurUrl(): string | null {
    return this.orgSlug ? `${this.base}/public/organisations/${this.orgSlug}/prescripteur` : null;
  }

  readonly flagIcon = flagIcon;

  // Généré 100% côté client (lib `qrcode`) : pas d'appel à un service tiers pour un QR qui
  // pointe vers une URL d'organisation, et le rendu est garanti identique à celui des formats
  // téléchargeables ci-dessous (même générateur, mêmes options).
  private async generatePrescripteurPreview(): Promise<void> {
    if (!this.prescripteurUrl) return;
    try {
      this.prescripteurQrDataUrl = await QRCode.toDataURL(this.prescripteurUrl, { width: 240, margin: 2 });
    } catch (e) {
      console.error('generatePrescripteurPreview error', e);
    }
  }

  toggleSearchEnabled(): void {
    if (!this.orgId || this.togglingSearchEnabled) return;
    const next = !this.searchEnabled;
    this.searchEnabled = next;
    this.togglingSearchEnabled = true;
    this.organisationsService.updateSettings(this.orgId, { prescripteur_search_enabled: next }).subscribe({
      next: () => {
        this.togglingSearchEnabled = false;
        this.toast.show(
          this.translate.instant('componentsWizard.qrModal.toasts.noted'),
          'success',
          4000,
          next
            ? this.translate.instant('componentsWizard.qrModal.toasts.searchEnabled')
            : this.translate.instant('componentsWizard.qrModal.toasts.searchDisabled')
        );
      },
      error: () => {
        this.searchEnabled = !next;
        this.togglingSearchEnabled = false;
        this.toast.show(this.translate.instant('componentsWizard.qrModal.toasts.error'), 'error', 4000, this.translate.instant('componentsWizard.qrModal.toasts.genericErrorParam'));
      },
    });
  }

  togglePrescripteurVisible(): void {
    if (!this.establishmentId || this.togglingPrescripteurVisible) return;
    const next = !this.prescripteurVisible;
    this.prescripteurVisible = next;
    this.togglingPrescripteurVisible = true;
    this.estabService.updateInfo(this.establishmentId, { prescripteur_visible: next }).subscribe({
      next: () => {
        this.togglingPrescripteurVisible = false;
        this.toast.show(
          this.translate.instant('componentsWizard.qrModal.toasts.noted'),
          'success',
          4000,
          next
            ? this.translate.instant('componentsWizard.qrModal.toasts.establishmentVisible')
            : this.translate.instant('componentsWizard.qrModal.toasts.establishmentHidden')
        );
      },
      error: () => {
        this.prescripteurVisible = !next;
        this.togglingPrescripteurVisible = false;
        this.toast.show(this.translate.instant('componentsWizard.qrModal.toasts.error'), 'error', 4000, this.translate.instant('componentsWizard.qrModal.toasts.genericErrorParam'));
      },
    });
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async downloadPrescripteurPng(): Promise<void> {
    const url = this.prescripteurUrl;
    if (!url) return;
    try {
      const dataUrl = await QRCode.toDataURL(url, { width: 1200, margin: 2 });
      const blob = await (await fetch(dataUrl)).blob();
      this.triggerDownload(blob, this.translate.instant('componentsWizard.qrModal.forecastQr.downloadFilenamePng'));
    } catch (e) {
      console.error('downloadPrescripteurPng error', e);
      alert(this.translate.instant('componentsWizard.qrModal.alerts.cannotDownloadQr'));
    }
  }

  async downloadPrescripteurSvg(): Promise<void> {
    const url = this.prescripteurUrl;
    if (!url) return;
    try {
      const svg = await QRCode.toString(url, { type: 'svg', margin: 2 });
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      this.triggerDownload(blob, this.translate.instant('componentsWizard.qrModal.forecastQr.downloadFilenameSvg'));
    } catch (e) {
      console.error('downloadPrescripteurSvg error', e);
      alert(this.translate.instant('componentsWizard.qrModal.alerts.cannotDownloadQr'));
    }
  }

  async downloadPrescripteurPdf(): Promise<void> {
    const url = this.prescripteurUrl;
    if (!url) return;
    try {
      const dataUrl = await QRCode.toDataURL(url, { width: 1200, margin: 2 });
      const w = window.open('', '_blank', 'width=900,height=1200');
      if (!w) return;
      const windowTitle = this.translate.instant('componentsWizard.qrModal.forecastQr.windowTitle');
      w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${windowTitle}</title>
        <style>@media print { @page { margin: 0; } body { margin: 0; } } body { margin: 0; display: grid; place-items: center; height: 100vh; } img { width: 60%; height: auto; }</style>
        </head><body><img src="${dataUrl}" onload="setTimeout(()=>window.print(), 50)" /></body></html>`);
      w.document.close();
    } catch (e) {
      console.error('downloadPrescripteurPdf error', e);
      alert(this.translate.instant('componentsWizard.qrModal.alerts.cannotGeneratePdf'));
    }
  }

  private computeWidgetUrl(): string {
    return this.linksWidget.startsWith('http') ? this.linksWidget : this.base + this.linksWidget;
  }

  copyWidgetUrl(): void {
    if (!this.linksWidget || !this.widgetEnabled) return;
    navigator.clipboard?.writeText(this.computeWidgetUrl()).then(() => {
      this.showCopyToast = true;
      setTimeout(() => this.showCopyToast = false, 2000);
    });
  }

  /** Une ligne par langue active du site : widget.html est traduit et fixe sa langue via ?lang=
   *  (voir i18n-patient.js côté backend) — pas de sélecteur en direct dans l'iframe embarquée. */
  get widgetLanguages(): { code: string; label: string; url: string }[] {
    if (!this.linksWidget) return [];
    const base = this.computeWidgetUrl();
    return this.establishmentLangues.map(code => ({
      code,
      label: PATIENT_LANGUAGES.find(l => l.code === code)?.label ?? code,
      url: base + (base.includes('?') ? '&' : '?') + 'lang=' + code,
    }));
  }

  copyWidgetUrlFor(url: string): void {
    if (!this.widgetEnabled) return;
    navigator.clipboard?.writeText(url).then(() => {
      this.showCopyToast = true;
      setTimeout(() => this.showCopyToast = false, 2000);
    });
  }

  toggleWidget(): void {
    if (!this.establishmentId || this.togglingWidget) return;
    const next = !this.widgetEnabled;
    this.widgetEnabled = next;
    this.togglingWidget = true;
    this.estabService.updateInfo(this.establishmentId, { widget_enabled: next }).subscribe({
      next: () => {
        this.togglingWidget = false;
        this.toast.show(
          this.translate.instant('componentsWizard.qrModal.toasts.noted'),
          'success',
          4000,
          next
            ? this.translate.instant('componentsWizard.qrModal.toasts.widgetEnabled')
            : this.translate.instant('componentsWizard.qrModal.toasts.widgetDisabled')
        );
      },
      error: () => {
        this.widgetEnabled = !next;
        this.togglingWidget = false;
        this.toast.show(this.translate.instant('componentsWizard.qrModal.toasts.error'), 'error', 4000, this.translate.instant('componentsWizard.qrModal.toasts.genericErrorParam'));
      },
    });
  }

  loadNearby(): void {
    if (!this.establishmentId) return;
    this.nearbyLoading = true;
    this.estabService.getNearby(this.establishmentId).subscribe({
      next: (config) => {
        this.nearbySelected = config.selected || [];
        this.nearbyCandidates = config.candidates || [];
        this.nearbyLoading = false;
      },
      error: () => {
        this.nearbyLoading = false;
      },
    });
  }

  addNearby(): void {
    if (!this.establishmentId || !this.nearbyPickerId || this.nearbyMutating) return;
    const id = this.establishmentId;
    const nearbyId = this.nearbyPickerId;
    this.nearbyMutating = true;
    this.estabService.addNearby(id, nearbyId).subscribe({
      next: () => {
        this.nearbyPickerId = '';
        this.nearbyMutating = false;
        this.loadNearby();
      },
      error: () => {
        this.nearbyMutating = false;
        this.toast.show(this.translate.instant('componentsWizard.qrModal.toasts.error'), 'error', 4000, this.translate.instant('componentsWizard.qrModal.toasts.cannotAddEstablishment'));
      },
    });
  }

  removeNearby(nearbyId: string): void {
    if (!this.establishmentId || this.nearbyMutating) return;
    const id = this.establishmentId;
    this.nearbyMutating = true;
    this.estabService.removeNearby(id, nearbyId).subscribe({
      next: () => {
        this.nearbyMutating = false;
        this.loadNearby();
      },
      error: () => {
        this.nearbyMutating = false;
        this.toast.show(this.translate.instant('componentsWizard.qrModal.toasts.error'), 'error', 4000, this.translate.instant('componentsWizard.qrModal.toasts.cannotRemoveEstablishment'));
      },
    });
  }

  printGuide(format: 'A6' | 'A5' | 'A4'): void {
    const w = window.open('', '_blank', 'width=900,height=1200');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${this.translate.instant('componentsWizard.qrModal.windowTitles.printPdf')}</title>
      <style>@media print { @page { size: ${format}; margin: 0; } body { margin: 0; } } body { margin: 0; } iframe { width: 100%; height: 100vh; border: none; }</style>
      </head><body><iframe src="/assets/Guide_scan.pdf" onload="setTimeout(()=>window.print(), 50)"></iframe></body></html>`);
    w.document.close();
  }

  onPrintQr(): void {
    this.printQrSheet();
  }

  onDownloadQr(): void {
    this.downloadQrSheet();
  }

  onPrintQueueQr(): void {
    this.printQueueQrSheet();
  }

  onDownloadQueueQr(): void {
    this.downloadQueueQrSheet();
  }

  private qrUrlSized(size: number): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=` + encodeURIComponent(this.base + this.linksPublic + '/mobile');
  }

  private queueQrUrlSized(size: number): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=` + encodeURIComponent(this.computeQueuePublicUrl());
  }

  private async getQrDataUrl(size = 512): Promise<string> {
    const url = this.qrUrlSized(size);
    const res = await fetch(url, { cache: 'no-store' });
    const blob = await res.blob();
    return await new Promise<string>((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(blob);
    });
  }

  private async getQueueQrDataUrl(size = 512): Promise<string> {
    const url = this.queueQrUrlSized(size);
    const res = await fetch(url, { cache: 'no-store' });
    const blob = await res.blob();
    return await new Promise<string>((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(blob);
    });
  }

  private async loadSheetImage(path = '/assets/QRCodeA4.png'): Promise<string> {
    const res = await fetch(path, { cache: 'no-store' });
    if (!res.ok) throw new Error('Impossible de charger l\'image');
    const blob = await res.blob();
    return await new Promise<string>((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(blob);
    });
  }

  private async composeSvgWithQr(imagePath = '/assets/QRCodeA4.png'): Promise<string> {
    const [imageDataUrl, qrDataUrl] = await Promise.all([this.loadSheetImage(imagePath), this.getQrDataUrl(1024)]);
    return imageDataUrl;
  }

  private async composeQueueSvgWithQr(svgPath = '/assets/QRCodeA5.svg'): Promise<string> {
    const [svgText, qrDataUrl] = await Promise.all([
      fetch(svgPath, { cache: 'no-store' }).then(r => r.text()),
      this.getQueueQrDataUrl(1024)
    ]);
    if (svgText.includes('id="qr-slot"')) {
      return svgText
        .replace(/(<image[^>]*id="qr-slot"[^>]*href=")[^"]*(")/, `$1${qrDataUrl}$2`)
        .replace(/(<image[^>]*id="qr-slot"[^>]*xlink:href=")[^"]*(")/, `$1${qrDataUrl}$2`);
    }
    if (svgText.includes('{{QR_URL}}')) return svgText.replaceAll('{{QR_URL}}', qrDataUrl);
    const m = svgText.match(/viewBox="([\d.\s-]+)"/);
    let injected = svgText;
    const size = 256;
    if (m) {
      const [minX, minY, w, h] = m[1].split(/\s+/).map(Number);
      const x = minX + (w - size) / 2;
      const y = minY + (h - size) / 2;
      injected = svgText.replace(/<\/svg>\s*$/i, `  <image href="${qrDataUrl}" x="${x}" y="${y}" width="${size}" height="${size}" id="qr-slot" />\n</svg>`);
    } else {
      injected = svgText.replace(/<\/svg>\s*$/i, `  <image href="${qrDataUrl}" x="100" y="100" width="${size}" height="${size}" id="qr-slot" />\n</svg>`);
    }
    return injected;
  }

  private async printQrSheet(): Promise<void> {
    try {
      const composed = await this.composeSvgWithQr();
      const blob = new Blob([composed], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const w = window.open('', '_blank', 'width=900,height=1200');
      if (!w) return;
      w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${this.translate.instant('componentsWizard.qrModal.windowTitles.printQr')}</title>
        <style>@media print { @page { size: A4; margin: 0; } body { margin: 0; } } body { margin: 0; display: grid; place-items: center; } img { width: 100%; height: auto; }</style>
        </head><body><img src="${url}" onload="setTimeout(()=>window.print(), 50)" /></body></html>`);
      w.document.close();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) {
      console.error('printQrSheet error', e);
      alert(this.translate.instant('componentsWizard.qrModal.alerts.cannotGeneratePrintSheet'));
    }
  }

  private async downloadQrSheet(): Promise<void> {
    try {
      const composed = await this.composeSvgWithQr();
      const blob = new Blob([composed], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'waitora-qr-a4.svg';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('downloadQrSheet error', e);
      alert(this.translate.instant('componentsWizard.qrModal.alerts.cannotPrepareDownload'));
    }
  }

  private async printQueueQrSheet(): Promise<void> {
    try {
      const composed = await this.composeQueueSvgWithQr();
      const blob = new Blob([composed], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const w = window.open('', '_blank', 'width=900,height=1200');
      if (!w) return;
      w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${this.translate.instant('componentsWizard.qrModal.windowTitles.printQueueQr')}</title>
        <style>@media print { @page { size: A4; margin: 0; } body { margin: 0; } } body { margin: 0; display: grid; place-items: center; } img { width: 100%; height: auto; }</style>
        </head><body><img src="${url}" onload="setTimeout(()=>window.print(), 50)" /></body></html>`);
      w.document.close();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) {
      console.error('printQueueQrSheet error', e);
      alert(this.translate.instant('componentsWizard.qrModal.alerts.cannotGeneratePrintSheet'));
    }
  }

  private async downloadQueueQrSheet(): Promise<void> {
    try {
      const [svgText, qrDataUrl] = await Promise.all([
        fetch('/assets/QRCodeA5.svg', { cache: 'no-store' }).then(r => r.text()),
        this.getQueueQrDataUrl(1024)
      ]);

      const composed = svgText.replace(
        /(<image[^>]*id="qr-slot"[^>]*)href="[^"]*"/,
        `$1href="${qrDataUrl}"`
      );

      const blob = new Blob([composed], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const w = window.open('', '_blank', 'width=900,height=1200');
      if (!w) return;
      w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${this.translate.instant('componentsWizard.qrModal.windowTitles.printPdf')}</title>
        <style>@page { size: A5 portrait; margin: 0; } * { margin: 0; padding: 0; box-sizing: border-box; } html, body { width: 148mm; height: 210mm; overflow: hidden; position: relative; } img { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; }</style>
        </head><body><img src="${url}" onload="setTimeout(()=>window.print(), 50)" /></body></html>`);
      w.document.close();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) {
      console.error('downloadQueueQrSheet error', e);
      alert(this.translate.instant('componentsWizard.qrModal.alerts.cannotPrepareDownload'));
    }
  }

  copyQueueUrl(): void {
    const url = this.computeQueuePublicUrl();
    navigator.clipboard?.writeText(url);
  }

  previewQueueUrl(): void {
    const url = this.computeQueuePublicUrl();
    window.open(url, '_blank');
  }

  /** Suffixe de fichier par langue (voir src/assets/QRCode{A4,A5,A6}_{suffixe}.svg) — le FR
   *  n'a pas de suffixe (fichier par défaut) et n'a donc pas d'entrée ici. */
  private readonly POSTER_LANG_SUFFIX: Record<string, string> = {
    ar: 'Arabe',
    en: 'Anglais',
    es: 'Espagnol',
    it: 'Italien',
    pt: 'Portugais',
  };

  /** Une ligne par langue du site disposant d'une affiche dédiée (hors FR, déjà couvert par le
   *  bouton "Télécharger QRcode" par défaut de chaque format). */
  get posterLanguages(): { code: string; label: string }[] {
    return this.establishmentLangues
      .filter(code => this.POSTER_LANG_SUFFIX[code])
      .map(code => ({ code, label: PATIENT_LANGUAGES.find(l => l.code === code)?.label ?? code }));
  }

  private posterAssetPath(format: 'A4' | 'A5' | 'A6', lang: string): string {
    const suffix = this.POSTER_LANG_SUFFIX[lang];
    return suffix ? `/assets/QRCode${format}_${suffix}.svg` : `/assets/QRCode${format}.svg`;
  }

  private posterPrintStyle(format: 'A4' | 'A5' | 'A6'): string {
    if (format === 'A5') {
      return '@page { size: A5 portrait; margin: 0; } * { margin: 0; padding: 0; box-sizing: border-box; } html, body { width: 148mm; height: 210mm; overflow: hidden; position: relative; } img { position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; }';
    }
    if (format === 'A6') {
      return '@media print { @page { size: A6; margin: 0; } body { margin: 0; } html, body { height: 100%; } } html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; } img { display: block; width: 100%; height: 100%; object-fit: cover; }';
    }
    return '@media print { @page { size: A4; margin: 0; } body { margin: 0; } } body { margin: 0; display: grid; place-items: center; } img { width: 100%; height: auto; }';
  }

  async downloadPoster(format: 'A4' | 'A5' | 'A6', lang = 'fr'): Promise<void> {
    try {
      const [svgText, qrDataUrl] = await Promise.all([
        fetch(this.posterAssetPath(format, lang), { cache: 'no-store' }).then(r => r.text()),
        this.getQrDataUrl(1024)
      ]);

      const composed = svgText.replace(
        /(<image[^>]*id="qr-slot"[^>]*)href="[^"]*"/,
        `$1href="${qrDataUrl}"`
      );

      const blob = new Blob([composed], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const w = window.open('', '_blank', 'width=900,height=1200');
      if (!w) return;
      w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${this.translate.instant('componentsWizard.qrModal.windowTitles.printPdf')}</title>
        <style>${this.posterPrintStyle(format)}</style>
        </head><body><img src="${url}" onload="setTimeout(()=>window.print(), 50)" /></body></html>`);
      w.document.close();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) {
      console.error('downloadPoster error', format, lang, e);
      alert(this.translate.instant('componentsWizard.qrModal.alerts.cannotDownloadFile'));
    }
  }

  downloadVideo(): void {
    const a = document.createElement('a');
    a.href = "/assets/Waitora - Vidéo d'attente - VF.mp4";
    a.download = "Waitora - Vidéo d'attente - VF.mp4";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  copyVideoUrl(): void {
    const url = 'https://www.youtube.com/embed/EBBQnIBx7OE?autoplay=1&mute';
    navigator.clipboard?.writeText(url).then(() => {
      this.showCopyToast = true;
      setTimeout(() => this.showCopyToast = false, 2000);
    });
  }

  toggleFullscreen(event: Event): void {
    const container = (event.target as HTMLElement).closest('.video-container');
    const video = container?.querySelector('video');
    if (video) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        video.requestFullscreen();
      }
    }
  }

}
